/**
 * Employee / manager self-service (contract Addendum E4, owner E-ESS).
 * Backed by routes/api/ess.php. Every endpoint acts on the caller's OWN employee record.
 */
import { api } from './client'

export interface EmployeeCard {
  id: string
  name: string
  first_name: string
  employee_code: string | null
  department: string | null
  designation: string | null
  date_of_joining: string | null
  work_state: string | null
  work_location: string | null
  employment_status: string | null
  manager: { id: string; name: string; employee_code: string | null } | null
  direct_reports: number
}

export interface EmployeeLink {
  linked: boolean
  role: string
  expects_employee: boolean
  employee: EmployeeCard | null
}

/** Reads answer `{linked:false, message}` for a login with no employee record. */
export interface Unlinked { linked: false; message: string }

export interface LeaveBalanceLite {
  leave_type_id: string; code: string; name: string
  balance: number; used: number; pending: number; entitlement: number; is_paid_leave: boolean
}

export interface EssSummary {
  linked: true
  employee: EmployeeCard
  today: {
    date: string
    day_class: 'WORKING' | 'HALF' | 'WEEK_OFF' | 'HOLIDAY' | null
    holiday: string | null
    shift: { name: string; start_time: string; end_time: string } | null
    status: string | null
    check_in: string | null
    check_out: string | null
    hours_worked: number | null
    on_leave: { id: string; type: string | null } | null
  }
  leave_balances: LeaveBalanceLite[]
  next_holiday: { date: string; name: string; optional: boolean; days_away: number } | null
  pending: Record<'leave' | 'regularisation' | 'reimbursement' | 'loan' | 'overtime' | 'profile_change' | 'tickets' | 'total', number>
  latest_payslip: { run_id: string; employee_id: string; month: number; year: number; status: string; net_pay: number; gross_earnings: number; total_deductions: number; previous: { month: number; year: number; net_pay: number } | null } | null
  tax: { financial_year: string; has_declaration: boolean; regime: string | null; status: string | null; proofs_uploaded: number; proofs_rejected: number }
  announcements: { id: string; title: string; type: string | null; priority: string | null; is_pinned: boolean; published_at: string | null; excerpt: string }[]
  policies_to_acknowledge: number
  onboarding: { instance_id: string; status: string; completion_percentage: number; my_open_tasks: number } | null
  exit: { id: string; status: string; last_working_date: string | null } | null
  review: { review_id: string | null; status: string | null; cycle: string | null; self_review_deadline: string | null; open_goals: number } | null
  assets_to_acknowledge: number
}

export interface CalendarDay {
  date: string
  label: string
  day_class: string
  holiday: string | null
  shift: string | null
  attendance_id: string | null
  status: string | null
  check_in: string | null
  check_out: string | null
  hours_worked: number | null
  late: boolean
  is_regularized: boolean
  regularisation: string | null
  leave: { id: string; type: string | null; code: string | null; status: string } | null
}

export interface EssCalendar {
  linked: true
  month: string
  employee_id: string
  /** Payroll closed or attendance locked for the month: no corrections. */
  closed: boolean
  days: CalendarDay[]
  totals: { present: number; late: number; absent: number; half_day: number; leave: number; holiday: number; week_off: number; hours: number }
}

export type TeamToday = 'PRESENT' | 'LATE' | 'ABSENT' | 'ON_LEAVE' | 'OFF' | 'NOT_IN_YET'

export interface EssTeam {
  linked: true
  manager: EmployeeCard
  date: string
  month: string
  counts: { total: number; present: number; late: number; absent: number; on_leave: number; not_working_today: number; yet_to_punch: number }
  members: {
    id: string; name: string; employee_code: string | null; designation: string | null; department: string | null
    today: TeamToday; check_in: string | null; check_out: string | null; shift: string | null
    review: { id: string; status: string; self_rating: number | null; manager_rating: number | null } | null
  }[]
  leaves: { id: string; employee_id: string; employee_name: string | null; type: string | null; code: string | null; start_date: string; end_date: string; days: number; status: string }[]
}

export interface ExitRow {
  id: string; status: string; resignation_date: string | null; last_working_date: string | null
  notice_period_days: number; exit_reason: string | null; approved_at: string | null; created_at: string | null; can_withdraw: boolean
}

export interface AssetState {
  asset_id: string
  acknowledged: boolean
  acknowledged_at: string | null
  open_request: { ticket_id: string; ticket_number: string; subject: string; status: string } | null
}

export interface OnboardingTaskRow {
  id: string; name: string; description: string | null; category: string | null; owner: string; mine: boolean
  is_mandatory: boolean; status: string; due_date: string | null; completed_at: string | null; notes: string | null
}
export interface MyOnboarding {
  instance_id: string; status: string; started_at: string | null; completion_percentage: number
  buddy: { id: string; name: string } | null
  tasks: OnboardingTaskRow[]
}

export async function getEmployeeLink(): Promise<EmployeeLink> {
  return (await api.get('/api/v1/me/employee-link')).data
}
export async function getEssSummary(): Promise<EssSummary | Unlinked> {
  return (await api.get('/api/v1/ess/summary')).data
}
export async function getEssCalendar(month: string, employeeId?: string): Promise<EssCalendar | Unlinked> {
  return (await api.get('/api/v1/ess/calendar', { params: { month, ...(employeeId ? { employee_id: employeeId } : {}) } })).data
}
export async function getEssTeam(month: string): Promise<EssTeam | Unlinked> {
  return (await api.get('/api/v1/ess/team', { params: { month } })).data
}
export async function getMyResignations(): Promise<{ linked: boolean; items: ExitRow[]; open: ExitRow | null; notice_period_days: number }> {
  return (await api.get('/api/v1/ess/resignation')).data
}
export async function resign(body: { last_working_date: string; exit_reason: string; resignation_date?: string }): Promise<{ message: string; exit: ExitRow }> {
  return (await api.post('/api/v1/ess/resignation', body)).data
}
export async function withdrawResignation(id: string, reason: string): Promise<{ message: string; exit: ExitRow }> {
  return (await api.post(`/api/v1/ess/resignation/${id}/withdraw`, { reason })).data
}
export async function getAssetStates(): Promise<AssetState[]> {
  const d = (await api.get('/api/v1/ess/assets/state')).data
  return d?.items ?? []
}
export async function acknowledgeAsset(id: string, note?: string): Promise<{ message: string; state: AssetState }> {
  return (await api.post(`/api/v1/ess/assets/${id}/acknowledge`, note ? { note } : {})).data
}
export async function requestAssetAction(id: string, type: 'ISSUE' | 'RETURN', description: string): Promise<{ message: string; state: AssetState; ticket: { id: string; ticket_number: string; status: string } }> {
  return (await api.post(`/api/v1/ess/assets/${id}/requests`, { type, description })).data
}
export async function getMyOnboarding(): Promise<MyOnboarding | null> {
  const d = (await api.get('/api/v1/ess/onboarding')).data
  return d?.onboarding ?? null
}
export async function completeMyOnboardingTask(taskId: string, note?: string): Promise<{ message: string; onboarding: MyOnboarding }> {
  return (await api.post(`/api/v1/ess/onboarding/tasks/${taskId}/complete`, note ? { note } : {})).data
}

/** Link / unlink an EXISTING login to an employee (HR; routes/api/people.php). */
export interface LinkableLogin { user_id: string; email: string; name: string; role: string; last_login_at: string | null }
export async function searchLinkableLogins(q: string): Promise<LinkableLogin[]> {
  const d = (await api.get('/api/v1/people/linkable-logins', { params: q ? { q } : {} })).data
  return d?.items ?? []
}
export async function linkLogin(employeeId: string, userId: string): Promise<{ message: string; notified: boolean }> {
  return (await api.post(`/api/v1/employees/${employeeId}/portal-access/link`, { user_id: userId })).data
}
export async function unlinkLogin(employeeId: string): Promise<{ message: string; notified: boolean }> {
  return (await api.delete(`/api/v1/employees/${employeeId}/portal-access/link`)).data
}

export function isLinked<T extends { linked: true }>(d: T | Unlinked | undefined): d is T {
  return Boolean(d && d.linked)
}

export const essKeys = {
  link: ['ess', 'link'] as const,
  summary: ['ess', 'summary'] as const,
  calendar: (month: string, employeeId?: string) => ['ess', 'calendar', month, employeeId ?? 'me'] as const,
  team: (month: string) => ['ess', 'team', month] as const,
  resignation: ['ess', 'resignation'] as const,
  assets: ['ess', 'assets', 'state'] as const,
  onboarding: ['ess', 'onboarding'] as const,
}

// ── Productivity (Addendum E6) ───────────────────────────────────────────────────────────
export interface MyRequest {
  type: 'leave' | 'regularisation' | 'reimbursement' | 'loan' | 'overtime' | 'profile' | 'ticket' | 'exit'
  type_label: string
  id: string
  title: string
  detail: string | null
  status: string
  submitted_at: string | null
  decided_at: string | null
  decided_by: string | null
  note: string | null
  link: string | null
  is_open: boolean
}
export async function getMyRequests(status: 'all' | 'open' | 'closed'): Promise<{ items: MyRequest[]; counts: { open: number; total: number } }> {
  const d = (await api.get('/api/v1/ess/requests', { params: { status } })).data
  return { items: d?.items ?? [], counts: d?.counts ?? { open: 0, total: 0 } }
}

export interface RegimeLine { taxable_income: number; deductions: number; annual_tax: number; monthly_tds: number }
export interface RegimeComparison {
  available: boolean; financial_year: string; message?: string; basis?: string; annual_gross?: number
  current_regime?: 'OLD' | 'NEW'; better_regime?: 'OLD' | 'NEW' | 'EITHER'; saving?: number
  old?: RegimeLine; new?: RegimeLine; has_declaration?: boolean
}
export async function getRegimeComparison(): Promise<RegimeComparison> {
  return (await api.get('/api/v1/ess/tax/regime-comparison')).data
}
